import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaFacebook, FaTwitter, FaInstagram, FaYoutube, FaTiktok } from 'react-icons/fa';
import { HiMail, HiPhone } from 'react-icons/hi';
import { MdCall } from 'react-icons/md';
import { firestore, firebase } from "../firebaseConfig";
import {
  sendAutoEmail,
  SUPPORT_AUTO_REPLY,
  TECHNICAL_AUTO_REPLY,
  isTechnicalSubject,
} from "../utils/emailTemplates";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await firestore.collection('contactMessages').add({
        ...formData,
        email: formData.email.toLowerCase().trim(),
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      });

      const autoReply = isTechnicalSubject(formData.subject)
        ? TECHNICAL_AUTO_REPLY(formData.name)
        : SUPPORT_AUTO_REPLY(formData.name);

      await sendAutoEmail({
        name: formData.name,
        email: formData.email,
        subject: 'We received your message — Veth Interiors',
        message: autoReply,
      });

      await sendAutoEmail({
        name: 'Veth Support',
        email: isTechnicalSubject(formData.subject)
          ? 'Web-ops@Vethinteriors.com'
          : 'support@vethinteriors.com',
        subject: `New contact: ${formData.subject}`,
        message: `From: ${formData.name} (${formData.email})\nPhone: ${formData.phone || 'N/A'}\n\n${formData.message}`,
      });

      setSubmitMessage('Thank you for your message! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      console.error(err);
      setSubmitMessage('Message saved. Email confirmation may be delayed — we will still respond to you.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const ContactInfo = ({ Icon, title, details, link }) => (
    <div className="bg-white dark:bg-amber-900 text-center shadow-lg rounded-lg p-6">
      <div className="flex items-center justify-center w-12 h-12 rounded-full mx-auto mb-4 bg-amber-100 dark:bg-amber-800">
        <Icon className="text-amber-600 text-2xl" />
      </div>
      <h3 className="text-xl font-semibold mb-2 text-black dark:text-white">{title}</h3>
      {link ? (
        <a href={link} className="text-amber-600 hover:text-amber-700 transition-colors">
          {details}
        </a>
      ) : (
        <p className="text-black dark:text-white">{details}</p>
      )}
    </div>
  );

  const SocialLink = ({ Icon, href, label }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center hover:bg-amber-700 transition-colors"
      aria-label={label}
    >
      <Icon className="text-xl" />
    </a>
  );

  return (
    <div className="dark:bg-gradient-to-r from-black to-amber-900 bg-white min-h-screen flex w-[100%] flex-col items-center text-center">
      {/* Hero Section */}
      <div className="relative pt-32 pb-16 dark:text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900 dark:text-white">
              Contact Us
            </h1>
            <p className="text-xl max-w-3xl mx-auto">
              Get in touch with our team. We're here to help bring your interior design vision to life.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Information Section */}
      <div className="py-16 dark:text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold  mb-4">
              Get In Touch
            </h2>
            <p className="text-lg  max-w-2xl mx-auto">
              Choose your preferred way to reach us
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <ContactInfo
              Icon={FaPhone}
              title="Phone / WhatsApp"
              details="08106079897"
              link="tel:+2348106079897"
            />
            <ContactInfo
              Icon={FaEnvelope}
              title="Support Email"
              details="support@vethinteriors.com"
              link="mailto:support@vethinteriors.com"
            />
            <ContactInfo
              Icon={FaMapMarkerAlt}
              title="Address"
              details="Abibatu Mogaji Shopping Complex, Lagos 101241, Lagos State, Nigeria"
            />
            <ContactInfo
              Icon={FaClock}
              title="Business Hours"
              details="Mon - Sat: 9:00 AM - 6:00 PM"
            />
            <ContactInfo
              Icon={HiMail}
              title="Admin / Operations"
              details="Web-ops@Vethinteriors.com"
              link="mailto:Web-ops@Vethinteriors.com"
            />
            <ContactInfo
              Icon={MdCall}
              title="WhatsApp"
              details="08106079897"
              link="https://wa.me/2348106079897"
            />
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <div className="py-16 dark:bg-gradient-to-r from-black to-amber-900 w-[100%] pb-10 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Send Us a Message
              </h2>
              <p className="text-lg text-white">
                Fill out the form below and we&apos;ll get back to you within 2–4 business hours
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="bg-white dark:bg-black p-8 rounded-lg shadow-lg text-left">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-black dark:text-white mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-amber-300 dark:border-amber-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent dark:bg-amber-900 dark:text-white"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-black dark:text-white mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-amber-300 dark:border-amber-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent dark:bg-amber-900 dark:text-white"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-black dark:text-white mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-amber-300 dark:border-amber-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent dark:bg-amber-900 dark:text-white"
                        placeholder="+234..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-black dark:text-white mb-2">
                        Subject *
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-amber-300 dark:border-amber-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent dark:bg-amber-900 dark:text-white"
                      >
                        <option value="">Select a subject</option>
                        <option value="general">General Inquiry</option>
                        <option value="support">Customer Support</option>
                        <option value="technical">Technical / Website Issue</option>
                        <option value="consultation">Design Consultation</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black dark:text-white mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-amber-300 dark:border-amber-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent dark:bg-amber-900 dark:text-white"
                      placeholder="Tell us about your inquiry..."
                    />
                  </div>

                  {submitMessage && (
                    <p className={`text-sm ${submitMessage.includes('Thank') ? 'text-green-600' : 'text-amber-600'}`}>
                      {submitMessage}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-amber-600 to-yellow-600 text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>

              <div className="space-y-8 text-left">
                <div className="bg-white dark:bg-black p-8 rounded-lg shadow-lg">
                  <h3 className="text-2xl font-bold text-black dark:text-white mb-6">
                    Why Choose VETH INTERIORS?
                  </h3>
                  <ul className="space-y-4 text-black dark:text-white">
                    <li className="flex items-start">
                      <span className="text-amber-600 mr-3">✓</span>
                      <span>Free initial consultation</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-600 mr-3">✓</span>
                      <span>Personalized design solutions</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-600 mr-3">✓</span>
                      <span>Premium quality materials</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-600 mr-3">✓</span>
                      <span>Timely project delivery</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white dark:bg-black p-8 rounded-lg shadow-lg">
                  <h3 className="text-2xl font-bold text-black dark:text-white mb-6">
                    Follow Us
                  </h3>
                  <div className="flex space-x-4">
                    <SocialLink Icon={FaInstagram} href="https://www.instagram.com/veth_interiorsventure" label="Instagram" />
                    <SocialLink Icon={FaTiktok} href="https://www.tiktok.com/@royal_son2" label="TikTok" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section — commented out */}
      {/* <div className="py-16 dark:bg-gradient-to-r from-black to-amber-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Visit Our Showroom
            </h2>
            <p className="text-lg text-white max-w-2xl mx-auto">
              Experience our designs in person at our beautiful showroom
            </p>
          </div>
          
          <div className="bg-white dark:bg-black rounded-lg shadow-lg p-8 max-w-4xl mx-auto">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-black dark:text-white mb-4">
                VETH INTERIORS Showroom
              </h3>
              <p className="text-black dark:text-white mb-6">
                400 Iponri Under Bridge U-Turn<br />
                Alaka, Surulere<br />
                Lagos 101241, Nigeria
              </p>
              <div className="bg-amber-100 dark:bg-amber-900 h-64 rounded-lg flex items-center justify-center">
                <p className="text-black dark:text-white">
                  Interactive map would be embedded here
                </p>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://maps.google.com/?q=400+Iponri+Under+Bridge+U-Turn,+Alaka,+Surulere,+Lagos+101241,+Nigeria"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-amber-700 transition-colors"
                >
                  Get Directions
                </a>
                <a
                  href="tel:+2349050729946"
                  className="border-2 border-amber-600 text-amber-600 dark:text-amber-400 px-6 py-3 rounded-lg font-semibold hover:bg-amber-600 hover:text-white transition-all duration-300"
                >
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {/* CTA Section */}
      {/* <div className="py-16 dark:bg-gradient-to-r from-black to-amber-900 w-[100%] pb-10 lg:py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-lg text-white mb-8">
              Let's discuss how we can transform your space into something extraordinary
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/about" className="bg-gradient-to-r from-amber-600 to-yellow-600 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transition-all duration-300">
                Learn More About Us
              </Link>
              <Link to="/shop" className="border-2 border-amber-600 text-amber-600 dark:text-amber-400 px-8 py-3 rounded-full font-semibold hover:bg-amber-600 hover:text-white transition-all duration-300">
                Browse Our Products
              </Link>
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default ContactUs;
